import { z } from 'zod';

// =============================================================================
// API Response Schemas (Zod at boundaries — Constitution Principle 10)
// =============================================================================

export const LandingPageBrandingSchema = z.object({
  primaryColor: z.string(),
  secondaryColor: z.string(),
  backgroundColor: z.string(),
  logoUrl: z.string().nullable().optional(),
  backgroundImageUrl: z.string().nullable().optional(),
  socialImageUrl: z.string().nullable().optional(),
}).passthrough();

export const LandingPageContentSchema = z.object({
  headline: z.string(),
  description: z.string(),
  ctaText: z.string(),
}).passthrough();

export const LandingPageSettingsSchema = z.object({
  showCountdown: z.boolean(),
  countdownEndDate: z.string().nullable().optional(),
  requireEmail: z.boolean(),
}).passthrough();

export const LandingPageGameOptionsSchema = z.object({
  showScoreAfterQuestion: z.boolean().default(true),
  showCorrectAnswer: z.boolean().default(true),
});

export const PublicEpisodeInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
  publishedAt: z.string().nullable().optional(),
  seasonId: z.string().nullable().optional(),
  seasonName: z.string().nullable().optional(),
  seasonNumber: z.number().nullable().optional(),
}).passthrough();

export const PublicShowInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
}).passthrough();

/**
 * Per-deployment feature flags carried on the by-slug payload.
 *
 * Forward-compat decision: optional at parse-time (defaulting to an empty
 * object). The play app derives `episodeOffersEnabled` per-call with a
 * defensive `=== true` check, so older backends that omit the field continue
 * to render defaults from the by-slug payload (no /offer call, no claim
 * resolution) without breaking the schema parse.
 */
export const LandingPageFeaturesSchema = z.object({
  episodeOffersEnabled: z.boolean().optional(),
}).passthrough();

export const PublicLandingPageSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  targetType: z.enum(['show', 'episode']),
  targetId: z.string(),
  organizationId: z.string(),
  branding: LandingPageBrandingSchema,
  content: LandingPageContentSchema,
  offerContent: z.string().nullable(),
  landingContent: z.string().nullable().default(null),
  settings: LandingPageSettingsSchema,
  gameOptions: LandingPageGameOptionsSchema,
  showYakOffer: z.boolean(),
  forwardUrl: z.string().nullable(),
  show: PublicShowInfoSchema.optional(),
  episode: PublicEpisodeInfoSchema.optional(),
  episodes: z.array(PublicEpisodeInfoSchema).optional(),
  features: LandingPageFeaturesSchema.optional(),
}).passthrough();

// =============================================================================
// Episode Game Data Schemas
// =============================================================================

export const VideoAssetSchema = z.object({
  url: z.string(),
});

export const QuestionResultSchema = z.object({
  questionId: z.string(),
  selectedAnswerId: z.string(),
  isCorrect: z.boolean(),
  timeToAnswer: z.number(),
});

export const AnswerSchema = z.object({
  id: z.string(),
  text: z.string(),
}).passthrough();

export const QuestionSchema = z.object({
  id: z.string(),
  text: z.string(),
  answers: z.array(AnswerSchema).min(2).max(4),
});

export const CuePointSchema = z.object({
  id: z.string(),
  type: z.enum(['question', 'interstitial', 'ad']),
  timeCode: z.number(),
  name: z.string().optional(),
  duration: z.number().optional(),
  asset: z.object({
    file: z.object({ url: z.string() }),
  }).optional(),
  question: QuestionSchema.optional(),
}).passthrough();

/**
 * Episode Game Data Schema (Single Video Model - FR-009)
 *
 * The play app uses a single combined video that includes intro/outro.
 * The `intro` and `outro` fields are kept optional for backwards compatibility
 * with the API, but only the `video` field is used in the play app.
 */
export const EpisodeGameDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  video: VideoAssetSchema,
  cuePoints: z.array(CuePointSchema),
  // Backwards compatibility - API may still send these but play app ignores them
  intro: VideoAssetSchema.nullable().optional(),
  outro: VideoAssetSchema.nullable().optional(),
  // No-accounts variant additions
  introEndSeconds: z.number().positive().optional(),
}).passthrough();

// =============================================================================
// Guest Session Schemas
// =============================================================================

export const GuestSessionResponseSchema = z.object({
  token: z.string().min(1),
  guestSessionId: z.string().min(1),
  expiresAt: z.string().min(1),
  episodeId: z.string().min(1),
  landingPageId: z.string().min(1),
  organizationId: z.string().min(1),
}).passthrough();

export const AnswerCheckResponseSchema = z.object({
  correct: z.boolean(),
  correctAnswerId: z.string().min(1),
  status: z.enum(['stored', 'replay']),
}).passthrough();

export const GuestGameSessionResponseSchema = z.object({
  status: z.enum(['stored', 'replay']),
  gameSession: z.object({
    id: z.string(),
    guestSessionId: z.string(),
    episodeId: z.string(),
    landingPageId: z.string(),
    organizationId: z.string(),
    score: z.number(),
    totalQuestions: z.number(),
    correctAnswers: z.number(),
    pointsPerCorrectAnswer: z.number(),
    pointsEarned: z.number(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    createdAt: z.string(),
    finalizeState: z.string(),
  }).passthrough(),
}).passthrough();

// =============================================================================
// Resolved Offer Response Schema (GET /v1/public/landing-pages/offer)
// =============================================================================

export const ResolvedOfferResponseSchema = z.object({
  offerContent: z.string().nullable(),
  forwardUrl: z.string().nullable(),
  showYakOffer: z.boolean(),
  /**
   * `override` — episode-specific override applied.
   * `default`  — landing-page defaults applied (no override).
   * `override_disabled` — override exists but the owning org has disabled
   *   overrides at the feature-flag level; the play app suppresses the offer
   *   page entirely (no CTA, no Yak banner) and shows the score-screen
   *   terminal state.
   */
  resolvedFrom: z.enum(['override', 'default', 'override_disabled']),
  resolutionToken: z.string().nullable(),
});

export type ResolvedOfferResponse = z.infer<typeof ResolvedOfferResponseSchema>;

// =============================================================================
// Analytics Event Schemas
// =============================================================================

export const PublicAnalyticsEventSchema = z.object({
  eventType: z.enum([
    'view',
    'unique_view',
    'game_start',
    'game_complete',
    'offer_view',
    'offer_click',
    'offer_dismiss',
  ]),
  sessionId: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
